import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Select,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { MagnifyingGlass } from 'phosphor-react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { InputForm } from '../../../../../../components/inputs/forms/InputForm';
import { useNfseEmissao } from '../../../../../../hooks/useNfseEmissao';
import { extractApiErrorMessage, extractErrorMessage } from '../../../../../../utils/ErrorUtils';
import { NAO_NIF_OPCOES } from '../../shared/constants';
import { MunicipioBusca } from '../../shared/MunicipioBusca';
import { island } from '../../shared/NfseIsland';
import { PaisBusca } from '../../shared/PaisBusca';
import { C, NButton } from '../../shared/ui';
import { FormSection } from './FormSection';

type Busca = 'idle' | 'encontrado' | 'nao_encontrado';

const EMAIL_VALIDO = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CAMPOS_DO_EXTERIOR = [
  'tomador_nif',
  'tomador_c_nao_nif',
  'tomador_pais',
  'tomador_cod_postal',
  'tomador_cidade',
  'tomador_estado',
];

export function TomadorSection({ isDisabled }: { isDisabled?: boolean }) {
  const {
    control,
    getValues,
    setValue,
    register,
    formState: { errors },
  } = useFormContext();
  const { getTomador } = useNfseEmissao();
  const exterior = !!useWatch({ name: 'tomador_exterior' });
  const nif = ((useWatch({ name: 'tomador_nif' }) as string) ?? '').trim();
  const motivoErro = errors.tomador_c_nao_nif as { message?: string } | undefined;
  const [buscando, setBuscando] = useState(false);
  const [busca, setBusca] = useState<Busca>('idle');
  // Município da consulta do CNPJ que não casou com o IBGE: fica digitado na busca (RF03).
  const [sugestao, setSugestao] = useState<{ nome: string; uf?: string | null } | null>(null);
  const documento = useWatch({ name: 'tomador_doc' }) as string;
  const email = ((useWatch({ name: 'tomador_email' }) as string) ?? '').trim();
  const temEmail = EMAIL_VALIDO.test(email);
  const municipio = (useWatch({ name: 'tomador_municipio_ibge' }) as string) ?? '';
  // RN21: quando o e-mail passa a ser válido (digitado ou trazido pela consulta), o envio
  // vem marcado; quando deixa de ser, desmarca. Só na troca: se a pessoa desmarcou, seguir
  // digitando não marca de novo. Chamar antes de gravar o e-mail novo no formulário.
  // Rascunho reaberto mantém o que foi gravado.
  const marcarEnvio = (valor?: string | null) => {
    const antes = EMAIL_VALIDO.test(String(getValues('tomador_email') ?? '').trim());
    const agora = EMAIL_VALIDO.test((valor ?? '').trim());
    if (antes !== agora) setValue('enviar_email', agora, { shouldDirty: true });
  };
  // A Sefin exige endereço completo quando o tomador é CNPJ (E0235).
  const exigeEndereco = String(documento ?? '').replace(/\D/g, '').length === 14;

  const buscar = async () => {
    const doc = String(getValues('tomador_doc') ?? '').replace(/\D/g, '');
    if (doc.length !== 14) {
      island.notify('A busca funciona com CNPJ', { tone: 'warn', meta: 'CPF: preencha à mão' });
      return;
    }
    setBuscando(true);
    try {
      const t = await getTomador(doc);
      const preencher = (campo: string, valor?: string | null) => {
        if (valor) setValue(campo, valor, { shouldValidate: true, shouldDirty: true });
      };
      preencher('tomador_nome', t.nome);
      if (t.email) {
        marcarEnvio(t.email);
        preencher('tomador_email', t.email);
      }
      preencher('tomador_municipio_ibge', t.municipio_ibge);
      setSugestao(!t.municipio_ibge && t.municipio ? { nome: t.municipio, uf: t.uf } : null);
      preencher('tomador_cep', t.cep);
      preencher('tomador_logradouro', t.logradouro);
      preencher('tomador_numero', t.numero);
      preencher('tomador_complemento', t.complemento);
      preencher('tomador_bairro', t.bairro);
      setBusca('encontrado');
      if (!t.municipio_ibge) {
        island.notify('Município não encontrado no IBGE', {
          tone: 'warn',
          meta: 'escolha na lista',
        });
      }
    } catch (err) {
      setBusca('nao_encontrado');
      island.notify(extractApiErrorMessage(err, 'Tomador não encontrado'), {
        tone: 'warn',
        meta: 'preencha à mão',
      });
    } finally {
      setBuscando(false);
    }
  };

  let ajuda: string | undefined;
  if (busca === 'encontrado') ajuda = 'Dados preenchidos pela consulta do CNPJ';
  if (busca === 'nao_encontrado') ajuda = 'Não encontrado: preencha à mão';

  let notaEndereco = exigeEndereco ? '(obrigatório para CNPJ)' : '(opcional para CPF)';
  if (exterior) notaEndereco = '(de onde o serviço é tomado)';

  return (
    <FormSection
      n={2}
      title="Tomador do serviço"
      subtitle={
        exterior
          ? 'NIF, país e endereço de onde o serviço é tomado'
          : 'Busca pelo CNPJ preenche nome, e-mail e endereço'
      }
    >
      <Controller
        control={control}
        name="tomador_exterior"
        render={({ field: { value, onChange } }) => (
          <Checkbox
            mb={4}
            size="sm"
            isChecked={!!value}
            isDisabled={isDisabled}
            onChange={e => {
              onChange(e.target.checked);
              setBusca('idle');
              // Campos que somem não ficam presos: limite neles travaria sem nada à vista.
              const ocultos = e.target.checked ? ['tomador_doc'] : CAMPOS_DO_EXTERIOR;
              ocultos.forEach(campo => setValue(campo, '', { shouldDirty: true }));
            }}
          >
            <Text fontSize="13px" color={C.ink}>
              Tomador no exterior (sem CNPJ/CPF)
            </Text>
          </Checkbox>
        )}
      />
      <SimpleGrid columns={{ base: 1, md: 2 }} columnGap={4}>
        {exterior ? (
          <Box>
            <InputForm
              name="tomador_nif"
              label="NIF do tomador"
              placeholder="Identificação fiscal no país dele"
              isDisabled={isDisabled}
            />
            {!nif && (
              <FormControl isRequired isInvalid={!!motivoErro} mb={5}>
                <FormLabel fontWeight="bold" mb={1} htmlFor="tomador_c_nao_nif">
                  Motivo de não ter NIF
                </FormLabel>
                <Select
                  id="tomador_c_nao_nif"
                  placeholder="Selecione"
                  isDisabled={isDisabled}
                  {...register('tomador_c_nao_nif')}
                >
                  {NAO_NIF_OPCOES.map(o => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{extractErrorMessage(motivoErro?.message)}</FormErrorMessage>
              </FormControl>
            )}
          </Box>
        ) : (
          <InputForm
            name="tomador_doc"
            label="CNPJ / CPF do tomador"
            placeholder="00.000.000/0000-00"
            isRequired
            isDisabled={isDisabled}
            helper={ajuda}
            helperColor={busca === 'encontrado' ? C.success : undefined}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                buscar();
              }
            }}
            buttonRight={
              <NButton
                variant="secondary"
                icon={MagnifyingGlass}
                ml={2}
                onClick={buscar}
                isLoading={buscando}
                isDisabled={isDisabled}
              >
                Buscar
              </NButton>
            }
          />
        )}
        <InputForm
          name="tomador_nome"
          label="Razão social / nome"
          isRequired
          isDisabled={isDisabled}
        />
        <Box>
          <InputForm
            name="tomador_email"
            label="E-mail para envio da nota"
            type="email"
            placeholder="financeiro@tomador.com.br"
            isDisabled={isDisabled}
            controlMb={2}
            onInputChange={valor => marcarEnvio(String(valor ?? ''))}
          />
          <Controller
            control={control}
            name="enviar_email"
            render={({ field: { value, onChange } }) => (
              <Checkbox
                mb={5}
                size="sm"
                isChecked={!!value && temEmail}
                isDisabled={isDisabled || !temEmail}
                onChange={e => onChange(e.target.checked)}
              >
                <Text fontSize="13px" color={temEmail ? C.ink : C.subtle}>
                  Enviar a nota por e-mail após autorizar
                </Text>
              </Checkbox>
            )}
          />
        </Box>
        {exterior ? (
          <PaisBusca
            name="tomador_pais"
            label="País do tomador"
            isRequired
            isDisabled={isDisabled}
          />
        ) : (
          <MunicipioBusca
            name="tomador_municipio_ibge"
            label="Município do tomador"
            isRequired={exigeEndereco}
            isDisabled={isDisabled}
            sugestao={sugestao}
            helper={
              sugestao && !municipio
                ? `"${sugestao.nome}" não bateu com o IBGE: escolha na lista`
                : undefined
            }
            helperColor={sugestao && !municipio ? C.warning : undefined}
          />
        )}
      </SimpleGrid>

      <Text fontSize="13px" fontWeight={700} color={C.muted} mt={1} mb={2}>
        Endereço {notaEndereco}
      </Text>
      <SimpleGrid columns={{ base: 1, md: 3 }} columnGap={4}>
        {exterior ? (
          <>
            <InputForm name="tomador_cidade" label="Cidade" isRequired isDisabled={isDisabled} />
            <InputForm
              name="tomador_estado"
              label="Estado / província / região"
              isRequired
              isDisabled={isDisabled}
            />
            <InputForm
              name="tomador_cod_postal"
              label="Código postal"
              isRequired
              isDisabled={isDisabled}
            />
          </>
        ) : (
          <InputForm
            name="tomador_cep"
            label="CEP"
            mask="99999-999"
            isRequired={exigeEndereco}
            isDisabled={isDisabled}
          />
        )}
        <InputForm
          name="tomador_bairro"
          label="Bairro"
          isRequired={exigeEndereco && !exterior}
          isDisabled={isDisabled}
        />
        <InputForm name="tomador_numero" label="Número" isDisabled={isDisabled} />
        <InputForm
          name="tomador_logradouro"
          label="Logradouro"
          isRequired={exigeEndereco || exterior}
          isDisabled={isDisabled}
          controlMb={0}
        />
        <InputForm
          name="tomador_complemento"
          label="Complemento"
          isDisabled={isDisabled}
          controlMb={0}
        />
      </SimpleGrid>
    </FormSection>
  );
}
